import { useState, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function InvoiceBanner() {
  const [customerNumber, setCustomerNumber] = useState('');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          const minWidth = 600;
          const minHeight = 336;
          
          let newWidth = img.width;
          let newHeight = img.height;
          const aspectRatio = img.width / img.height;

          // Check if image needs resizing
          if (newWidth < minWidth || newHeight < minHeight) {
            // Scale up to meet minimum requirements while maintaining aspect ratio
            if (minWidth / aspectRatio >= minHeight) {
              // Width is the limiting factor
              newWidth = minWidth;
              newHeight = minWidth / aspectRatio;
            } else {
              // Height is the limiting factor
              newHeight = minHeight;
              newWidth = minHeight * aspectRatio;
            }
          }

          canvas.width = newWidth;
          canvas.height = newHeight;
          
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          resolve(canvas.toDataURL('image/jpeg', 0.9));
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setIsProcessing(true);
    try {
      const resizedImage = await resizeImage(file);
      setUploadedImage(resizedImage);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Failed to process image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleSend = () => {
    // Placeholder for send functionality
    console.log('Send clicked', { customerNumber, uploadedImage });
  };

  const isFormValid = customerNumber.trim() !== '' && uploadedImage !== null;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          {/* Title Banner */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Upload Invoice Banner
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Upload your custom banner image for invoices. The image will be automatically resized to meet the required dimensions while maintaining aspect ratio.
            </p>
          </div>

          {/* Main Upload Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Customer Number Field */}
            <div className="mb-8">
              <label htmlFor="customerNumber" className="block text-sm font-semibold text-slate-700 mb-3">
                Customer Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="customerNumber"
                value={customerNumber}
                onChange={(e) => setCustomerNumber(e.target.value)}
                placeholder="Enter your customer number"
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:border-[#29B3E6] transition-colors text-slate-900 placeholder:text-slate-400"
              />
            </div>

            {/* Drag and Drop Area */}
            <div className="relative">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-3 border-dashed rounded-xl transition-all duration-300 ${
                  isDragging
                    ? 'border-[#29B3E6] bg-[#E6F5FD]'
                    : 'border-slate-300 bg-slate-50'
                } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <div className="p-12 text-center">
                  <div className="mb-6">
                    <i className="ri-image-add-line text-6xl text-slate-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    {isProcessing ? 'Processing image...' : 'Drag & Drop your image here'}
                  </h3>
                  <p className="text-slate-500 mb-6">
                    or click to browse from your computer
                  </p>
                  <button
                    type="button"
                    onClick={handleBrowseClick}
                    disabled={isProcessing}
                    className="px-6 py-3 bg-[#29B3E6] text-white rounded-lg hover:bg-[#1E9BD1] transition-colors font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Browse Files
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                  <p className="text-sm text-slate-400 mt-4">
                    Minimum dimensions: 600×336px (landscape)
                  </p>
                </div>
              </div>

              {/* Send Button - Top Right */}
              <button
                onClick={handleSend}
                disabled={!isFormValid}
                className={`absolute -top-4 -right-4 px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-all duration-300 shadow-lg ${
                  isFormValid
                    ? 'bg-[#29B3E6] text-white hover:bg-[#1E9BD1] hover:shadow-xl cursor-pointer'
                    : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                }`}
              >
                <i className="ri-send-plane-fill mr-2"></i>
                Send
              </button>
            </div>

            {/* Preview Section */}
            {uploadedImage && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  Preview
                </h3>
                <div className="bg-slate-50 rounded-xl p-6 shadow-md border border-slate-200 flex justify-center items-center">
                  <div className="relative">
                    <img
                      src={uploadedImage}
                      alt="Uploaded banner preview"
                      className="max-w-full h-auto rounded-lg shadow-lg"
                    />
                    <button
                      onClick={() => setUploadedImage(null)}
                      className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center shadow-lg cursor-pointer"
                      title="Remove image"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Information Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100">
              <div className="w-12 h-12 bg-[#E6F5FD] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-aspect-ratio-line text-2xl text-[#29B3E6]"></i>
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Auto Resize</h4>
              <p className="text-sm text-slate-600">
                Images are automatically resized to meet minimum dimensions while maintaining aspect ratio
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100">
              <div className="w-12 h-12 bg-[#E6F5FD] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-landscape-line text-2xl text-[#29B3E6]"></i>
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Landscape Format</h4>
              <p className="text-sm text-slate-600">
                Optimized for landscape images with minimum 600×336px dimensions
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100">
              <div className="w-12 h-12 bg-[#E6F5FD] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-eye-line text-2xl text-[#29B3E6]"></i>
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Live Preview</h4>
              <p className="text-sm text-slate-600">
                See your uploaded banner in real-time before sending
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
